import { useState, useEffect, useCallback } from "react";
import { AppHeader } from "@/components/AppHeader";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Settings, RotateCcw, Save } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import ScoreWidget from "@/components/widgets/ScoreWidget";
import EventTimelineWidget from "@/components/widgets/EventTimelineWidget";
import PlayerTableWidget from "@/components/widgets/PlayerTableWidget";
import DraggableWidget from "@/components/widgets/DraggableWidget";

interface League {
  id: string;
  name: string;
}

interface Gameweek {
  id: string;
  gw_number: number;
  status: string;
}

interface Matchup {
  team1: string;
  team2: string;
  score1: number;
  score2: number;
  status: 'live' | 'upcoming' | 'final';
}

interface Filter {
  id: string;
  label: string;
  selected: boolean;
}

interface WidgetLayout {
  id: string;
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface MatchData {
  home_team: string;
  away_team: string;
  home_score: number;
  away_score: number;
  status: string;
  win_probability?: number;
  sport: 'soccer' | 'csgo';
}

const MatchCenter = () => {
  const { user } = useAuth();
  const [selectedSport, setSelectedSport] = useState<'soccer' | 'csgo'>('soccer');
  const [matchData, setMatchData] = useState<MatchData | null>(null);
  const [widgets, setWidgets] = useState<WidgetLayout[]>([]);
  const [draggedWidget, setDraggedWidget] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<any[]>([]);
  const [players, setPlayers] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState<string>('points');

  useEffect(() => {
    if (user) {
      loadUserLayout();
      loadMatchData();
    }
  }, [user, selectedSport]);

  const getDefaultWidgets = useCallback((): WidgetLayout[] => {
    const baseWidgets = [
      { id: 'score', type: 'score', x: 0, y: 0, w: 2, h: 1 },
      { id: 'timeline', type: 'timeline', x: 2, y: 0, w: 2, h: 1 },
      { id: 'players', type: 'players', x: 0, y: 1, w: 4, h: 2 },
    ];

    if (selectedSport === 'soccer') {
      return [
        ...baseWidgets,
        { id: 'position-summary', type: 'position-summary', x: 4, y: 0, w: 2, h: 1 },
        { id: 'xg-chart', type: 'xg-chart', x: 4, y: 1, w: 2, h: 1 },
      ];
    } else {
      return [
        ...baseWidgets,
        { id: 'round-timeline', type: 'round-timeline', x: 4, y: 0, w: 2, h: 1 },
        { id: 'kill-feed', type: 'kill-feed', x: 4, y: 1, w: 2, h: 1 },
      ];
    }
  }, [selectedSport]);

  const loadUserLayout = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_layouts')
        .select('layout_json')
        .eq('user_id', user.id)
        .eq('sport', selectedSport)
        .maybeSingle();

      if (error) throw error;

      if (data?.layout_json) {
        setWidgets(data.layout_json as any);
      } else {
        setWidgets(getDefaultWidgets());
      }
    } catch (error) {
      console.error('Error loading user layout:', error);
      setWidgets(getDefaultWidgets());
    } finally {
      setLoading(false);
    }
  };

  const saveUserLayout = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_layouts')
        .upsert({
          user_id: user.id,
          sport: selectedSport,
          layout_json: widgets as any
        });

      if (error) throw error;

      toast({
        title: "Layout saved",
        description: "Your widget layout has been saved successfully."
      });
    } catch (error) {
      console.error('Error saving layout:', error);
      toast({
        title: "Error",
        description: "Failed to save layout. Please try again.",
        variant: "destructive"
      });
    }
  };

  const resetLayout = () => {
    setWidgets(getDefaultWidgets());
    toast({
      title: "Layout reset",
      description: "Widget layout has been reset to default."
    });
  };

  const loadMatchData = async () => {
    // Mock match data for demo
    setMatchData({
      home_team: "Team A",
      away_team: "Team B", 
      home_score: 2,
      away_score: 1,
      status: "live",
      win_probability: 65,
      sport: selectedSport
    });

    // Mock events
    setEvents([
      { id: '1', minute: 15, type: 'goal', player: 'John Doe', team: 'Team A', timestamp: new Date().toISOString() },
      { id: '2', minute: 32, type: 'assist', player: 'Jane Smith', team: 'Team A', timestamp: new Date().toISOString() },
    ]);

    // Mock players
    setPlayers([
      { id: '1', name: 'John Doe', position: 'FWD', team: 'Team A', minutes: 45, points: 8.5, goals: 1, assists: 0, shots: 3, status: 'playing' },
      { id: '2', name: 'Jane Smith', position: 'MID', team: 'Team A', minutes: 45, points: 6.2, goals: 0, assists: 1, shots: 1, status: 'playing' },
    ]);
  };

  const handleDragStart = (widgetId: string) => {
    setDraggedWidget(widgetId);
  };

  const handleDragEnd = () => {
    setDraggedWidget(null);
  };

  const renderWidget = (widget: WidgetLayout) => {
    const commonProps = {
      id: widget.id,
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
      isDragging: draggedWidget === widget.id
    };

    switch (widget.type) {
      case 'score':
        return <ScoreWidget {...commonProps} matchup={matchData} />;
      case 'timeline':
        return <EventTimelineWidget {...commonProps} events={events} sport={selectedSport} />;
      case 'players':
        return (
          <PlayerTableWidget 
            {...commonProps}
            players={players}
            sport={selectedSport}
            sortBy={sortBy}
            onSort={setSortBy}
          />
        );
      default:
        return (
          <DraggableWidget {...commonProps} title={`${widget.type} Widget`}>
            <div className="text-center text-muted-foreground py-8">
              {widget.type.replace('-', ' ').toUpperCase()} widget content
            </div>
          </DraggableWidget>
        );
    }
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <AppHeader />
          <div className="p-6 space-y-6">
            <Skeleton className="h-16 w-full" />
            <div className="grid grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Skeleton key={i} className="h-64 w-full" />
              ))}
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <AppHeader />
        <div className="p-6 space-y-6">
          {/* Header Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-bold">Match Center</h1>
              <Select value={selectedSport} onValueChange={setSelectedSport as (value: string) => void}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="soccer">⚽ Soccer</SelectItem>
                  <SelectItem value="csgo">🎯 CS:GO</SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="outline">
                {matchData?.status || 'No Match'}
              </Badge>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={resetLayout}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset Layout
              </Button>
              <Button size="sm" onClick={saveUserLayout}>
                <Save className="h-4 w-4 mr-2" />
                Save Layout
              </Button>
            </div>
          </div>

          {/* Draggable Widget Grid */}
          <div className="grid grid-cols-6 gap-4 min-h-[600px]">
            {widgets.map((widget) => (
              <div
                key={widget.id}
                className="col-span-2"
                style={{
                  gridColumn: `span ${widget.w}`,
                  gridRow: `span ${widget.h}`
                }}
              >
                {renderWidget(widget)}
              </div>
            ))}
          </div>

          {/* Instructions */}
          <div className="text-center text-sm text-muted-foreground">
            Drag widgets to reorder them. Use the Save Layout button to persist your arrangement.
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default MatchCenter;